import * as apiPostSearch from 'api/erpRecipe/PostErpRecipeSearchV1';
import * as apiPut from 'api/erpRecipe/PutErpRecipeIdV1';
import * as Ingredient from 'types/Recipe/Detail/ErpNew/Ingredient';
import * as OrderGuide from 'types/Recipe/Detail/ErpNew/OrderGuide';
import * as Product from 'types/Recipe/Detail/ErpNew/Product';
import * as ProductList from 'types/Recipe/Detail/ErpNew/ProductList';

export type Type = Ingredient.Type[];

export function create(): Type {
	return [];
}

export function toggleProductSelection(
	obj: Type,
	ingredientId: string,
	productId: string,
	segmentIndex: number
): Type {
	return obj.map((e) => {
		if (e.segmentIndex !== segmentIndex) return e;
		return Ingredient.toggleProductSelection(e, ingredientId, productId);
	});
}

export function mapFirstSuggestionToProduct(obj: Type): Type {
	return obj.map(Ingredient.mapFirstSuggestionToProduct);
}

export function getSelectedProductId(
	obj: Type,
	segmentIndex: number,
	ingredientId: string
): string | null {
	for (const i of obj) {
		if (i.segmentIndex !== segmentIndex) continue;
		if (i.ingredientId !== ingredientId) continue;
		const product = i.products.find((e) => e.selected);
		if (!product) continue;
		return product.productId;
	}
	return null;
}

export function getSelectedProduct(
	obj: Type,
	segmentIndex: number,
	ingredientId: string
): Product.Type | undefined {
	for (const i of obj) {
		if (i.segmentIndex !== segmentIndex) continue;
		if (i.ingredientId !== ingredientId) continue;
		const product = ProductList.getSelected(i.products, i.manuallySearchedProducts);
		if (!product) continue;
		return product;
	}
	return undefined;
}

export function getProduct(
	obj: Type,
	input: {
		ingredientId: string;
		segmentIndex: number;
		productId: string;
	}
): Product.Type | undefined {
	for (const ingredient of obj) {
		const product = Ingredient.getProduct(ingredient, input);
		if (!product) continue;
		return product;
	}

	return undefined;
}

export function getProducts(
	obj: Type,
	input: {
		ingredientId: string;
		segmentIndex: number;
	}
): Product.Type[] {
	for (const ingredient of obj) {
		if (ingredient.ingredientId !== input.ingredientId) continue;
		if (ingredient.segmentIndex !== input.segmentIndex) continue;
		return ingredient.products;
	}
	return [];
}

export function mergeWithProducts(obj: Type, products: ProductList.Type): Type {
	return obj.map((ingredient) => {
		if (ingredient.products.length > 0) return ingredient;
		return {
			...ingredient,
			products: products.filter(
				(product) => product.ingredientId === ingredient.ingredientId
			),
		};
	});
}

export function mapToApi(obj: Type): apiPut.Mapping[] {
	return obj.map((ingredient) => {
		const product = ProductList.getSelected(
			ingredient.products,
			ingredient.manuallySearchedProducts
		);
		return {
			...ingredient,
			ingredientId: ingredient.ingredientId,
			productId: product?.productId ?? null,
			sourceId: product?.sourceId ?? null,
			segmentIndex: ingredient.segmentIndex,
			name: product?.name ?? '',
			imageUrl: product?.imageUrl ?? '',
			price: String(product?.price ?? 0),
			gtin: product?.gtin ?? '',
		};
	});
}

export function mapFromApiSearch(mappings: apiPostSearch.Mapping[]): Type {
	return mappings.map(Ingredient.mapFromApiSearch);
}

export function applyOrderGuidePriority(obj: Type, orderGuide: OrderGuide.Type): Type {
	return obj.map((e) => Ingredient.applyOrderGuidePriority(e, orderGuide));
}

export function getOnlySelectedProducts(obj: Type): Type {
	return obj.map((e) => {
		return { ...e, products: e.products.filter((e) => e.selected) ?? null };
	});
}

export function applyIngredientIdToProducts(obj: Type): Type {
	return obj.map((e) => {
		return Ingredient.applyIngredientIdToProducts(e);
	});
}

export function sortProducts(obj: Type): Type {
	return obj.map((e) => {
		return { ...e, products: ProductList.sortByPriority(e.products) };
	});
}

export function sortIngredients(
	obj: Ingredient.Type[],
	sortOrder: { id: string; index: number }[]
): Type {
	const remainingIngredients = [...obj];
	const result: Type = [];

	for (const order of sortOrder) {
		const index = remainingIngredients.findIndex(
			(p) => p.ingredientId === order.id && p.segmentIndex === order.index
		);

		if (index === -1) continue;
		result.push(remainingIngredients[index]);
		remainingIngredients.splice(index, 1);
	}
	return [...result, ...remainingIngredients];
}

export function mapToSortOrder(list: Type): { id: string; index: number }[] {
	return list.map((e) => ({ id: e.ingredientId, index: e.segmentIndex }));
}

export function getDefaultProduct(obj: Type, ingredientId: string): Product.Type | undefined {
	for (const ingredient of obj) {
		const product = Ingredient.getDefaultProduct(ingredient, ingredientId);
		if (!product) continue;
		return product;
	}

	return undefined;
}

export async function searchAndAddToManuallySearchedProducts(
	ingredients: Type,
	ingredientId: string,
	searchTerm: string,
	nrOfMatches: number,
	orderGuide: OrderGuide.Type
): Promise<Type> {
	const output: Type = [];
	for (const i of ingredients) {
		if (i.ingredientId !== ingredientId) {
			output.push(i);
			continue;
		}
		output.push(
			await Ingredient.searchAndAddToManuallySearchedProducts(
				i,
				searchTerm,
				nrOfMatches,
				orderGuide
			)
		);
	}
	return output;
}

export function resetManuallySearchedProducts(list: Type, ingredientId: string): Type {
	return list.map((e) => {
		if (e.ingredientId !== ingredientId) return e;
		return Ingredient.resetManuallySearchedProducts(e);
	});
}
